import * as React from 'react';
import { Dispatch, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ElementLoader } from 'ui';
import { ApplicationState } from 'app/types-store';
import { useSyncScroll } from 'app/area/case-report/case-table-view/hooks/sync-scroll-hooks';
import { actionCreators } from '../actions';
import { CaseDraftsTableHeader } from './case-drafts-table-header';
import { CaseDraftsTableRows } from './case-drafts-table-rows';
import { CaseDraftsTableMobile } from './case-drafts-table-mobile';
import { IntlShape } from 'react-intl';
import { MyCaseDraftsTranslation } from 'app/area/my-case-drafts/constants';
import styles from './case-drafts-table.scss';

interface CaseDraftsTableProps {
  translation: MyCaseDraftsTranslation;
  intl: IntlShape;
}

export const CaseDraftsTable = ({ translation, intl }: CaseDraftsTableProps) => {
  const dispatch: Dispatch<any> = useDispatch();
  const defaultTableState = { rows: [], fetching: false, deleting: false };
  const { rows, fetching } = useSelector(
    (state: ApplicationState) => state.myCaseDrafts?.caseDraftsTable ?? defaultTableState
  );

  const tableViewRef = useRef<HTMLDivElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { handleScroll, tableWidth, tableColumnWidths } = useSyncScroll({ tableContainerRef, resultsLoaded: !fetching });

  const columns = useMemo(() => [
    '',
    translation.my_case_drafts_id,
    translation.my_case_drafts_case_type,
    translation.my_case_drafts_last_modified_date,
    translation.my_case_drafts_title,
    translation.my_case_drafts_description,
    translation.my_case_drafts_case_date
  ], [translation]);

  useEffect(() => {
    dispatch(actionCreators.loadDrafts(translation.my_case_drafts_fail_to_load));
  }, [dispatch]);

  const onDelete = useCallback((draftId: number) => {
    dispatch(actionCreators.deleteDraft(draftId, translation.my_case_drafts_fail_to_delete));
  }, [dispatch, translation]);

  if (fetching) {
    return <ElementLoader />;
  }

  if (!rows.length) {
    return <p className="no-content">{translation.my_case_drafts_no_results}</p>;
  }

  return (
    <div className={styles.caseDraftsWrapper} ref={tableViewRef}>
      <div className={styles.caseDraftsTableContainer}>
        <div className={styles.tableView}>
          <CaseDraftsTableHeader
            columns={columns}
            tableWidth={tableWidth}
            columnWidths={tableColumnWidths}
            containerRef={headerContainerRef}
            handleScroll={handleScroll}
          />
          <CaseDraftsTableRows
            rows={rows}
            onDelete={onDelete}
            translation={translation}
            intl={intl}
            tableContainerRef={tableContainerRef}
            portalTarget={tableViewRef.current}
          />
        </div>
      </div>
      <div className={styles.cardView}>
        <CaseDraftsTableMobile rows={rows} onDelete={onDelete} translation={translation} intl={intl} />
      </div>
    </div>
  );
};
